import api from './client';
import type {
  PipelineTypeInfo,
  PipelineExecutionListItem,
  PipelineExecutionResponse,
  PipelineProgress,
  PipelineRunRequest,
  PipelineScheduleListItem,
  PipelineScheduleResponse,
  PipelineScheduleCreate,
  PipelineScheduleUpdate,
  AutomationStatus,
  AutomationSummary,
} from '@/types/data';

export const automationApi = {
  // パイプラインタイプ一覧を取得
  getPipelineTypes: async (): Promise<PipelineTypeInfo[]> => {
    const response = await api.get<PipelineTypeInfo[]>('/api/automation/pipeline-types');
    return response.data;
  },

  // パイプラインタイプ情報を取得
  getPipelineType: async (pipelineType: string): Promise<PipelineTypeInfo> => {
    const response = await api.get<PipelineTypeInfo>(`/automation/pipeline-types/${pipelineType}`);
    return response.data;
  },

  // パイプラインを実行
  runPipeline: async (request: PipelineRunRequest): Promise<PipelineExecutionResponse> => {
    const response = await api.post<PipelineExecutionResponse>('/api/automation/run', request);
    return response.data;
  },

  // パイプラインをキャンセル
  cancelPipeline: async (executionId: number): Promise<PipelineExecutionResponse> => {
    const response = await api.post<PipelineExecutionResponse>(`/automation/executions/${executionId}/cancel`);
    return response.data;
  },

  // パイプライン実行を取得
  getExecution: async (executionId: number): Promise<PipelineExecutionResponse> => {
    const response = await api.get<PipelineExecutionResponse>(`/automation/executions/${executionId}`);
    return response.data;
  },

  // パイプライン進捗を取得
  getProgress: async (executionId: number): Promise<PipelineProgress> => {
    const response = await api.get<PipelineProgress>(`/automation/executions/${executionId}/progress`);
    return response.data;
  },

  // パイプライン実行履歴を取得
  listExecutions: async (params: {
    company_id?: number;
    pipeline_type?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PipelineExecutionListItem[]> => {
    const response = await api.get<PipelineExecutionListItem[]>('/api/automation/executions', { params });
    return response.data;
  },

  // スケジュール作成
  createSchedule: async (data: PipelineScheduleCreate): Promise<PipelineScheduleResponse> => {
    const response = await api.post<PipelineScheduleResponse>('/api/automation/schedules', data);
    return response.data;
  },

  // スケジュール一覧を取得
  listSchedules: async (params: {
    company_id?: number;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<PipelineScheduleListItem[]> => {
    const response = await api.get<PipelineScheduleListItem[]>('/api/automation/schedules', { params });
    return response.data;
  },

  // スケジュールを取得
  getSchedule: async (scheduleId: number): Promise<PipelineScheduleResponse> => {
    const response = await api.get<PipelineScheduleResponse>(`/automation/schedules/${scheduleId}`);
    return response.data;
  },

  // スケジュールを更新
  updateSchedule: async (scheduleId: number, data: PipelineScheduleUpdate): Promise<PipelineScheduleResponse> => {
    const response = await api.put<PipelineScheduleResponse>(`/automation/schedules/${scheduleId}`, data);
    return response.data;
  },

  // スケジュールを削除
  deleteSchedule: async (scheduleId: number): Promise<void> => {
    await api.delete(`/automation/schedules/${scheduleId}`);
  },

  // 法人の自動化ステータスを取得
  getStatus: async (companyId: number): Promise<AutomationStatus> => {
    const response = await api.get<AutomationStatus>('/api/automation/status', {
      params: { company_id: companyId },
    });
    return response.data;
  },

  // 全体のサマリーを取得（管理者用）
  getSummary: async (): Promise<AutomationSummary> => {
    const response = await api.get<AutomationSummary>('/api/automation/summary');
    return response.data;
  },
};
